import {
  LANG,
  PROFILES_REQUIRING_ASSIGNED_BRANCHES,
  RECRUITMENT_METHOD,
  SCHOOL_NOT_SET,
} from "constants/constants";
import {
  ROLE_ADA,
  ROLE_ADV,
  ROLE_COMPTA,
  ROLE_EVT_SF_CR,
  ROLE_EVT_SF_VW,
  ROLE_FORMATION,
  ROLE_GP,
  ROLE_IA,
  ROLE_LT,
  ROLE_MGR,
  ROLE_NDF_ADV,
  ROLE_NDF_COMPTA,
  ROLE_NDF_GP,
  ROLE_NDF_MANAGER,
  ROLE_NDF_SEC,
  ROLE_RADV,
  ROLE_RGN_ASSIST,
  ROLE_RGP,
} from "access-roles/roles";
import { extractValue, safeValue } from "./helper";

export const formatInfo = (info) =>
  Object.keys(info).reduce(
    (prev, curr) => ({
      ...prev,
      [curr]: { value: info[curr], error: false },
    }),
    {}
  );

export const formatOptionsTranslated = ({ id, lang, type, list = [] }) =>
  list
    ?.filter((elm) => elm.id === id)
    .map((elm) => ({
      value: elm.id,
      label: lang === LANG.FR ? elm.fr : elm.en,
      type,
    }));

export const formatNames = (list, id) =>
  list
    ?.filter((elm) => elm.id === Number(id))
    ?.map((elm) => ({
      value: elm.id,
      label: elm.full_name || elm.name,
    }));

export const cleanObject = (obj) => {
  if (obj === null || obj === "") {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj
      .map((item) =>
        typeof item === "object" && item !== null ? cleanObject(item) : item
      )
      .filter((item) => item !== null && item !== "");
  }
  if (typeof obj === "object") {
    const entries = Object.entries(obj)
      .map(([key, value]) => {
        if (value === null || value === "") {
          return null;
        }
        if (typeof value === "object") {
          const cleaned = cleanObject(value);
          if (
            (typeof cleaned === "object" &&
              key !== "assigned_branches" &&
              Object.keys(cleaned).length === 0) ||
            cleaned === null ||
            cleaned === ""
          ) {
            return null;
          }
          return [key, cleaned];
        }
        return [key, value];
      })
      .filter((entry) => entry !== null);

    return Object.fromEntries(entries);
  }
  return obj;
};

const getFirstValue = (arr) =>
  Array.isArray(arr) && arr.length > 0 ? arr[0].value : null;

export const formatCreatedData = (data) => {
  const branchId = getFirstValue(data?.branch_id);
  const managerId = getFirstValue(data?.manager_id);
  const leavingReasonId = getFirstValue(data?.leaving_reason_id);
  const nationalityId =
    getFirstValue(data?.nationality_id) || data.nationality_id?.id || null;
  const nationality_id = data?.nationality?.id || data.nationality[0]?.value;
  const profileId = getFirstValue(data?.profile_id);
  const recruitmentModeId = getFirstValue(data?.recruitment_mode_id);
  const gpId = getFirstValue(data?.gp_id);
  const godfatherId = data?.godfather_id ?? null;
  const heritageOfId = data?.heritage_of_id ?? null;
  const exConsultant = data?.ex_consultant ?? false;
  const enabledUser = data?.enabled ?? false;
  const visaType = data?.visa_type ?? null;
  const visa = Boolean(visaType);
  const visaExpiration = data?.visa_expiration_date ?? null;
  const visaNumber = data?.visa_number ?? null;
  const visaStatus = data?.visa_status ?? "";
  const userLanguage = data?.user_language ?? null;
  const comment = data?.comment ?? "";
  const siteLanguage = data?.site_language ?? null;
  const assignedBranches = data?.assigned_branches?.length
    ? data.assigned_branches.map((e) => e.value)
    : [];
  const cegidId = data?.cegid_id;
  const roleArray = Array.isArray(data.role)
    ? data.role.reduce((acc, elm) => {
        if (typeof elm === "string") return acc.concat(elm);
        if (elm && typeof elm === "object" && typeof elm.value === "string") {
          return acc.concat(elm.value);
        }
        return acc;
      }, [])
    : [];

  const startDate = data.start_date;

  const coefficient = {
    start_date: startDate,
    syntec_coef_position: data.syntec_coef_position ?? null,
    syntec_coef_position_staff: data.syntec_coef_position_staff ?? null,
  };

  const contract = {
    branch_id: branchId,
    contract_type: data.contract_type,
    start_date: startDate,
    end_date: data.end_date ?? null,
    leaving_reason_id: leavingReasonId,
  };

  const history_manager = {
    establishment_date: startDate,
    establishment_date_end:
      data.history_manager?.establishment_date_end ?? null,
    manager_id: managerId,
  };

  const person = {
    arrival_date: startDate,
    comment,
    dav_function: String(data.dav_function || ""),
    first_name: data.first_name,
    gender: data.gender,
    godfather_id: godfatherId,
    last_name: data.last_name,
    phone_number: data.phone_number,
    profile_id: String(profileId || ""),
    recruitment_mode_id: recruitmentModeId,
    referer_external: safeValue(data.referer_external) ?? null,
    referer_internal_id: extractValue(data.referer_internal_id) ?? null,
    school: data.school,
    school_level: data.school_level,
    site_language: siteLanguage,
  };

  const staff = {
    address: data.address,
    birth_place: data.birth_place,
    birthday: data.birthday,
    degree: data.degree,
    degree_year: data.degree_year,
    ex_consultant: exConsultant,
    gp_id: gpId,
    heritage_of_id: heritageOfId,
    nationality_id: nationalityId || nationality_id,
    personal_email: data.personal_email,
    visa,
    visa_expiration_date: visa ? visaExpiration : null,
    visa_number: visa ? visaNumber : null,
    visa_status: visa ? visaStatus : "",
    visa_type: visaType,
    assigned_branches: assignedBranches,
    cegid_id: cegidId,
  };

  const user = {
    email: data.email,
    picture_file_id: data.picture_file_id,
    enabled: enabledUser,
    role: roleArray,
    user_language: userLanguage,
  };

  const result = {
    coefficient,
    contract,
    history_manager,
    person,
    staff,
    user,
  };

  return cleanObject(result);
};

export const formatReceivedData = (
  data,
  {
    branches,
    davFunctions,
    lang,
    leavingReasons,
    recruitmentMethodList,
    profileList,
    roles,
    nationalitiesList,
    users,
    intl,
  }
) => {
  return {
    ...data,
    school:
      data.school === SCHOOL_NOT_SET
        ? intl.formatMessage({
            id: "to_be_completed",
            defaultMessage: "À compléter",
          })
        : data.school,
    recruitment_mode_id: formatOptionsTranslated({
      id: data?.recruitment_mode_id,
      lang,
      list: recruitmentMethodList,
    }),
    referer_internal_id:
      data.recruitment_mode_id === RECRUITMENT_METHOD.REFERER_INTERNAL
        ? formatNames(users, data?.referer_internal_id)
        : null,
    dav_function: formatOptionsTranslated({
      id: Number(data?.dav_function),
      lang,
      list: davFunctions,
    }),
    // Derived from the job by the cascade (redmine 12940): the mission no
    // longer stores its category, so the API does not return one. The field
    // starts empty and fills itself once the referential and the job are there.
    domain_category_id: "",
    domain_id: formatOptionsTranslated({
      id: Number(data?.domain_id),
      lang,
      list: davFunctions,
    }),
    branch_id: formatNames(branches, data.branch_id),
    cegid_id: data?.cegid_id ?? "",
    assigned_branches: branches
      .filter((elm) => data?.assigned_branches?.includes(elm.id))
      .map((elm) => ({
        value: elm.id,
        label: elm.name,
      })),
    profile_id: formatOptionsTranslated({
      id: data?.profile_id,
      lang,
      list: profileList,
    }),
    leaving_reason_id: formatOptionsTranslated({
      id: data?.leaving_reason_id,
      lang,
      list: leavingReasons,
    }),
    roles: roles?.filter((elm) => data?.role?.includes(elm.value)),
    syntec_coef_position_staff: data?.userCoefPosition,
    syntec_coef_position: data?.userCoefPosition,
    manager_id: formatNames(users, data?.manager_id),
    gp_id: formatNames(users, data?.gp_id),
    end_date: data?.end_date ?? null,
    nationality_id:
      nationalitiesList && (data.nationality_id || data.nationality?.id)
        ? nationalitiesList
            .filter((elm) =>
              data.nationality_id
                ? elm.id === data.nationality_id
                : elm.id === data.nationality?.id
            )
            .map((elm) => ({
              label:
                lang === LANG.FR ? elm.nationality_fr : elm.nationality_eng,
              value: elm.id,
            }))
        : [],
  };
};

export const getName = ({ list, id, type }) =>
  list
    ?.filter((elm) => elm?.id === id)
    .map((elm) => ({
      label: elm?.full_name || elm.name,
      value: elm?.id,
      type,
    }));

export const getConsultantName = ({ workers, id }) =>
  workers
    .filter((elm) => elm.id === id)
    .map((elm) => elm?.first_name + " " + elm?.last_name)[0];

export const getWorkerProfile = ({ workers, id }) =>
  workers.find((elm) => elm.id === id)?.profile_id || "";

export const getClientName = ({ clients, id }) =>
  clients.filter((elm) => elm?.id === id).map((elm) => elm?.name)[0];

export const formatListWithUpdatedUser = (list = [], users = []) => {
  return list.map((elm) => {
    const user = users.find((usr) => usr.id === elm.updated_by);
    return {
      ...elm,
      updated_by: user?.full_name || user?.name || null,
    };
  });
};
export const formatStaffName = (staff) =>
  `${staff.first_name} ${staff.last_name}`;

const createRole = (value, type = "roles") => ({
  value,
  label: value,
  type,
});

const commonRoles = [createRole(ROLE_EVT_SF_CR), createRole(ROLE_EVT_SF_VW)];

const finRoles = [createRole(ROLE_COMPTA), createRole(ROLE_NDF_COMPTA)];
const adaRoles = [createRole(ROLE_ADA)];

const additionalRoles = {
  adv: [createRole(ROLE_NDF_ADV), createRole(ROLE_ADV)],
  radv: [
    createRole(ROLE_NDF_ADV),
    createRole(ROLE_RADV),
    createRole(ROLE_NDF_MANAGER),
    createRole(ROLE_MGR),
  ],
  gp: [createRole(ROLE_NDF_GP), createRole(ROLE_GP)],
  rgp: [
    createRole(ROLE_RGP),
    createRole(ROLE_NDF_MANAGER),
    createRole(ROLE_MGR),
  ],
  ada: adaRoles,
  rada: [...adaRoles, createRole(ROLE_MGR)],
  fin: [...finRoles, createRole(ROLE_EVT_SF_VW)],
  rfin: [...finRoles, createRole(ROLE_MGR)],
  rbu: [createRole(ROLE_IA)],
  sec: [createRole(ROLE_NDF_SEC)],
  asr: [createRole(ROLE_RGN_ASSIST)],
  pdt: [createRole(ROLE_NDF_MANAGER), createRole(ROLE_LT)],
  forma: [createRole(ROLE_FORMATION)],
  ia: [createRole(ROLE_NDF_MANAGER), createRole(ROLE_IA)],
  mgr: [createRole(ROLE_IA)],
  dsoc: [createRole(ROLE_IA)],
  dg: [createRole(ROLE_IA)],
  rdd: [createRole(ROLE_IA)],
};

const profileWithCommonRole = [
  "adv",
  "radv",
  "gp",
  "rgp",
  "mgr",
  "dsoc",
  "si",
  "ada",
  "rh",
  "sec",
  "dfin",
  "dg",
  "pdt",
  "ia",
  "rbu",
  "dsi",
  "cdc",
  "ads",
  "rada",
  "rfin",
];

const managerProfiles = ["mgr", "dsoc", "dg", "dfin", "dsi", "rbu"];

export const rolesByProfileAutocomplete = (profileId) => {
  let roles = [...(additionalRoles[profileId] || [])];
  if (profileWithCommonRole.includes(profileId)) {
    roles = [...roles, ...commonRoles];
  }

  if (managerProfiles.includes(profileId)) {
    roles.push(createRole(ROLE_NDF_MANAGER), createRole(ROLE_MGR));
  }

  return roles;
};

export const isAssignedBranchesRequiredHelper = ({ profile_id }) =>
  PROFILES_REQUIRING_ASSIGNED_BRANCHES.includes(profile_id);
